<template>
  <div class="scheme-payment-wrapper m-left-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Toast Notification -->
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Back Button & Title -->
        <div class="d-flex align-center">
          <v-btn
            icon
            small
            class="back-btn mr-3"
            @click="$router.push('/CustomerSchemeList')"
            title="Back to Customer Schemes"
          >
            <v-icon color="#0f172a" size="20">mdi-arrow-left</v-icon>
          </v-btn>
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-wallet-membership</v-icon>
          </div>
          <div>
            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
              <span class="page-title">SCHEME PAYMENT LEDGER</span>
              <span
                class="status-pill"
                :class="schemeDetails.schemeStatus === 'Active' ? 'status-active' : 'status-closed'"
                v-if="schemeDetails.schemeStatus"
              >
                {{ schemeDetails.schemeStatus }}
              </span>
              <span class="overdue-warning-pill" v-if="schemeDetails.schemeStatus === 'Closed'">
                <v-icon x-small color="#dc2626" class="mr-1">mdi-alert-circle</v-icon>
                Overdue / Closed
              </span>
            </div>
            <div class="page-subtitle">
              {{ schemeDetails.goldSchemeId ? schemeDetails.goldSchemeId.goldSchemeName : 'Gold Scheme' }}
              <span v-if="schemeDetails.customerId && schemeDetails.customerId.customerName" class="ml-2">
                &bull; {{ schemeDetails.customerId.customerName }}
              </span>
              <span v-if="schemeDetails.customerId && schemeDetails.customerId.mobileNumber" class="ml-2 text-muted">
                ({{ schemeDetails.customerId.mobileNumber }})
              </span>
            </div>
          </div>
        </div>

        <!-- Right: Actions -->
        <div class="d-flex align-center flex-wrap" style="gap: 10px; row-gap: 8px;">
          <v-btn
            v-if="schemeDetails.schemeStatus === 'Active' && pendingInstallmentsCount > 0"
            dark
            color="#9a6b27"
            class="rounded-lg text-capitalize elevation-1"
            @click="openPayAll()"
          >
            <v-icon left small>mdi-cash-multiple</v-icon>
            Pay All Remaining ({{ pendingInstallmentsCount }})
          </v-btn>
          <v-btn
            icon
            small
            title="Refresh Ledger"
            class="refresh-icon-btn"
            @click="schemePayment"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>
          <v-btn
            outlined
            color="#64748b"
            class="rounded-lg text-capitalize erp-btn-secondary"
            @click="$router.push('/CustomerSchemeList')"
          >
            <v-icon left small>mdi-format-list-bulleted</v-icon>
            All Customer Schemes
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Top Overview: Customer Identity + KPI Metric Cards -->
    <v-row class="mb-4" dense>
      <!-- Left: Customer Profile Summary Card -->
      <v-col cols="12" md="4">
        <v-card flat class="customer-profile-card rounded-lg pa-4 fill-height">
          <div class="d-flex align-center mb-3">
            <div class="customer-avatar-monogram mr-3">
              {{ getCustomerInitials(schemeDetails.customerId ? schemeDetails.customerId.customerName : '') }}
            </div>
            <div class="overflow-hidden">
              <div class="customer-name-heading text-truncate">
                {{ schemeDetails.customerId ? schemeDetails.customerId.customerName : 'Customer' }}
              </div>
              <div class="customer-phone-sub">
                <v-icon x-small color="#64748b" class="mr-1">mdi-phone-outline</v-icon>
                {{ schemeDetails.customerId ? schemeDetails.customerId.mobileNumber : '-' }}
              </div>
            </div>
          </div>
          <v-divider class="mb-3"></v-divider>
          <div class="customer-info-list">
            <div class="info-row" v-if="schemeDetails.customerId && schemeDetails.customerId.address">
              <span class="info-label">Address:</span>
              <span class="info-value">{{ schemeDetails.customerId.address }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Scheme:</span>
              <span class="info-value scheme-tag">
                <v-icon x-small color="#9a6b27" class="mr-1">mdi-gold</v-icon>
                {{ schemeDetails.goldSchemeId ? schemeDetails.goldSchemeId.goldSchemeName : '-' }}
              </span>
            </div>
            <div class="info-row" v-if="schemeDetails.startDate">
              <span class="info-label">Start Date:</span>
              <span class="info-value">{{ formatDate(schemeDetails.startDate) }}</span>
            </div>
            <div class="info-row" v-if="schemeDetails.matureDate">
              <span class="info-label">Maturity Date:</span>
              <span class="info-value font-weight-bold">{{ formatDate(schemeDetails.matureDate) }}</span>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- Right: 4 KPI Summary Cards -->
      <v-col cols="12" md="8">
        <div class="kpi-grid">
          <!-- 1. Monthly Deposit -->
          <div class="kpi-card">
            <div class="kpi-content">
              <div class="kpi-label">MONTHLY DEPOSIT</div>
              <div class="kpi-value">₹ {{ formatCurrency(monthlyDepositAmount) }}</div>
              <div class="kpi-hint">Fixed installment amount</div>
            </div>
            <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
              <v-icon size="20" color="#2563eb">mdi-calendar-check-outline</v-icon>
            </div>
          </div>

          <!-- 2. Installments Paid -->
          <div class="kpi-card">
            <div class="kpi-content">
              <div class="kpi-label">INSTALLMENTS PAID</div>
              <div class="kpi-value">{{ totalPaidCount }} <span class="kpi-unit">/ {{ totalInstallmentsCount }}</span></div>
              <div class="kpi-hint">₹ {{ formatCurrency(totalPaidAmountSum) }} paid so far</div>
            </div>
            <div class="kpi-icon-pill" style="background: #f0fdf4; color: #16a34a;">
              <v-icon size="20" color="#16a34a">mdi-check-decagram-outline</v-icon>
            </div>
          </div>

          <!-- 3. Accrued Interest -->
          <div class="kpi-card">
            <div class="kpi-content">
              <div class="kpi-label">TOTAL ACCRUED INTEREST</div>
              <div class="kpi-value highlight-gold">₹ {{ formatDecimals(totalAccruedInterest) }}</div>
              <div class="kpi-hint">Accrued scheme bonus</div>
            </div>
            <div class="kpi-icon-pill" style="background: #fefce8; color: #ca8a04;">
              <v-icon size="20" color="#ca8a04">mdi-trending-up</v-icon>
            </div>
          </div>

          <!-- 4. Pending Amount -->
          <div class="kpi-card">
            <div class="kpi-content">
              <div class="kpi-label">PENDING BALANCE</div>
              <div class="kpi-value">₹ {{ formatCurrency(totalPendingAmount) }}</div>
              <div class="kpi-hint">{{ totalInstallmentsCount - totalPaidCount }} installments pending</div>
            </div>
            <div class="kpi-icon-pill" style="background: #faf5ff; color: #9333ea;">
              <v-icon size="20" color="#9333ea">mdi-clock-outline</v-icon>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Installment Master Table -->
    <v-card flat class="table-card rounded-lg overflow-hidden mb-4">
      <div class="card-top-bar px-4 py-3 d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon small color="#475569" class="mr-2">mdi-calendar-month-outline</v-icon>
          <span class="card-heading">INSTALLMENT SCHEDULE</span>
          <span class="items-count-badge ml-2" v-if="PaymentList && PaymentList.length > 0">
            {{ PaymentList.length }} Months
          </span>
        </div>
      </div>
      <v-divider></v-divider>

      <div class="table-responsive">
        <v-simple-table dense class="scheme-ledger-table m-stack-table m-stack-grid">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" style="width: 50px;">#</th>
                <th class="text-left">Month</th>
                <th class="text-left">Due Date</th>
                <th class="text-right">Monthly Deposit</th>
                <th class="text-right">Interest Rate</th>
                <th class="text-right">Tenure Covered</th>
                <th class="text-right">Interest Accrued</th>
                <th class="text-right">Paid Amount</th>
                <th class="text-center">Status</th>
                <th class="text-center" style="min-width: 150px;">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(value, i) in PaymentList" :key="value._id || i" class="table-row-hover">
                <td data-label="#" class="font-weight-medium text-muted">
                  {{ page == 1 ? i + 1 : i + 1 + 20 * (page - 1) }}
                </td>
                <td data-label="Month" class="font-weight-semibold">
                  <div class="d-flex align-center">
                    <v-icon x-small color="#9a6b27" class="mr-1">mdi-calendar</v-icon>
                    <span>{{ value.paidMonth || '-' }}</span>
                    <span v-if="value.paidYear" class="text-muted ml-1" style="font-size: 11.5px;">{{ value.paidYear }}</span>
                  </div>
                </td>
                <td data-label="Due Date">
                  <span class="date-chip" v-if="value.dueDate">{{ formatDate(value.dueDate) }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td data-label="Monthly Deposit" class="text-right font-weight-medium">
                  ₹ {{ formatCurrency(value.monthlyFixedAmount) }}
                </td>
                <td data-label="Interest Rate" class="text-right">
                  <span class="rate-badge" v-if="value.monthlyInterestRate">{{ value.monthlyInterestRate }}%</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td data-label="Tenure Covered" class="text-right">
                  <span v-if="value.monthsCovered">{{ value.monthsCovered }} mos</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td data-label="Interest Accrued" class="text-right font-weight-medium text-emerald">
                  <span v-if="value.monthlyInterestAmount != null">₹ {{ formatDecimals(value.monthlyInterestAmount) }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td data-label="Paid Amount" class="text-right font-weight-bold" :class="value.isPaid ? 'text-emerald' : 'text-muted'">
                  ₹ {{ formatCurrency(value.amountPaid || 0) }}
                </td>
                <td data-label="Status" class="text-center">
                  <span v-if="value.isPaid" class="badge-status-paid">
                    <v-icon x-small color="#16a34a" class="mr-1">mdi-check-circle</v-icon>
                    Paid
                  </span>
                  <span v-else-if="schemeDetails.schemeStatus === 'Closed'" class="badge-status-overdue">
                    Overdue
                  </span>
                  <span v-else-if="isPreviousMonth(value.paidMonth, value.paidYear)" class="badge-status-due">
                    Due
                  </span>
                  <span v-else class="badge-status-upcoming">
                    Upcoming
                  </span>
                </td>
                <td data-label="Action" class="text-center">
                  <!-- When Paid: Download Receipt + Reverse Action -->
                  <div class="d-flex align-center justify-center" style="gap: 6px;" v-if="value.isPaid">
                    <v-btn
                      small
                      outlined
                      color="#16a34a"
                      class="btn-receipt-action text-capitalize"
                      @click="downloadReceipt(value._id)"
                      title="Download PDF Receipt"
                    >
                      <v-icon left x-small>mdi-download</v-icon>
                      Receipt
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      class="btn-reverse-action"
                      title="Reverse / Cancel Payment"
                      @click="(selectedReverseId = value._id), (reverseDialog = true)"
                    >
                      <v-icon small color="#ef4444">mdi-undo-variant</v-icon>
                    </v-btn>
                  </div>

                  <!-- When Not Paid and Payable: Pay Now button -->
                  <div v-else-if="schemeDetails.schemeStatus !== 'Closed' && isPreviousMonth(value.paidMonth, value.paidYear)">
                    <v-btn
                      small
                      dark
                      color="#9a6b27"
                      class="btn-pay-now text-capitalize"
                      @click="makePayment2(value)"
                    >
                      <v-icon left x-small>mdi-credit-card-outline</v-icon>
                      Pay Now
                    </v-btn>
                  </div>

                  <!-- When Future/Not Yet Payable -->
                  <div v-else>
                    <span class="text-muted" style="font-size: 12px;">-</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>

      <!-- Pagination -->
      <div class="d-flex justify-space-between align-center px-4 py-3 border-t" v-if="Pagelength > 1">
        <div class="text-muted" style="font-size: 12.5px;">
          Page {{ page }} of {{ Pagelength }}
        </div>
        <v-pagination
          dense
          v-model="page"
          :length="Pagelength"
          :total-visible="7"
          color="#9a6b27"
        ></v-pagination>
      </div>
    </v-card>

    <!-- Record Payment Dialog -->
    <v-dialog width="460px" v-model="paymentDialog">
      <v-form v-model="addcat" ref="addcat" @submit.prevent>
        <v-card class="rounded-lg modal-card">
          <div class="modal-header px-5 py-4 d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <div class="modal-icon-pill mr-3" style="background: #fefce8; color: #9a6b27;">
                <v-icon size="18" color="#9a6b27">mdi-cash-plus</v-icon>
              </div>
              <div>
                <div class="modal-title">{{ payAllMode ? 'Pay All Remaining Installments' : 'Record Installment Payment' }}</div>
                <div class="modal-subtitle">
                  <span v-if="payAllMode">{{ pendingInstallmentsCount }} installments</span>
                  <span v-else>{{ paymentDetails1.paidMonth }} {{ paymentDetails1.paidYear }}</span>
                  &bull; {{ schemeDetails.customerId ? schemeDetails.customerId.customerName : '' }}
                </div>
              </div>
            </div>
            <v-btn icon small @click="paymentDialog = false, resetForm()">
              <v-icon color="#64748b">mdi-close</v-icon>
            </v-btn>
          </div>
          <v-divider></v-divider>

          <div class="pa-5">
            <!-- Amount -->
            <div class="mb-4">
              <label class="field-label">{{ payAllMode ? 'Total Amount (₹)' : 'Installment Amount (₹)' }}</label>
              <v-text-field
                readonly
                outlined
                dense
                hide-details
                color="#9a6b27"
                prefix="₹"
                class="pro-input erp-readonly font-weight-bold"
                :value="formatCurrency(payAllMode ? totalPendingAmount : paymentDetails1.monthlyFixedAmount)"
              ></v-text-field>
              <div v-if="payAllMode" class="text-caption text-muted mt-1">
                {{ pendingInstallmentsCount }} × ₹{{ formatCurrency(monthlyDepositAmount) }} — the scheme matures once paid
              </div>
            </div>

            <!-- Payment Date -->
            <div class="mb-4">
              <label class="field-label">Payment Date</label>
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="customPaidDate"
                    outlined
                    dense
                    readonly
                    color="#9a6b27"
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                    prepend-inner-icon="mdi-calendar"
                    class="pro-input"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="customPaidDate"
                  color="#9a6b27"
                  :max="todayDate"
                  @change="dateMenu = false"
                ></v-date-picker>
              </v-menu>
            </div>

            <!-- Payment Type -->
            <div class="mb-2">
              <label class="field-label">Payment Method <span class="required-star">*</span></label>
              <v-select
                :items="paymentTypeList"
                v-model="paytype"
                placeholder="Select payment method"
                dense
                color="#9a6b27"
                hide-details
                outlined
                class="pro-input"
                prepend-inner-icon="mdi-credit-card-outline"
              ></v-select>
            </div>
          </div>

          <v-divider></v-divider>
          <div class="px-5 py-3 d-flex justify-end align-center bg-modal-footer">
            <v-btn text color="#64748b" class="mr-2 text-capitalize btn-cancel" @click="paymentDialog = false, resetForm()">
              Cancel
            </v-btn>
            <v-btn
              dark
              color="#9a6b27"
              class="btn-primary text-capitalize px-5"
              @click="valid()"
            >
              <v-icon left small>mdi-check</v-icon>
              {{ payAllMode ? 'Confirm & Pay All' : 'Confirm Payment' }}
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- Reverse Payment Dialog -->
    <v-dialog width="440px" v-model="reverseDialog">
      <v-card class="rounded-lg modal-card pa-2">
        <div class="pa-5 text-center">
          <div class="delete-icon-box mb-3 mx-auto">
            <v-icon size="24" color="#dc2626">mdi-undo-variant</v-icon>
          </div>
          <div class="delete-title mb-2">Reverse Installment Payment</div>
          <div class="delete-subtitle">
            Are you sure you want to reverse this paid installment entry? This will adjust scheme totals and customer balance.
          </div>
        </div>
        <v-divider></v-divider>
        <div class="px-5 py-3 d-flex justify-end align-center bg-modal-footer">
          <v-btn text color="#64748b" class="mr-2 text-capitalize btn-cancel" @click="reverseDialog = false">
            Cancel
          </v-btn>
          <v-btn color="#dc2626" dark class="text-capitalize px-4 rounded-lg" @click="reversePayment()">
            <v-icon left small>mdi-undo</v-icon>
            Confirm Reversal
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "GoldSchemePayment",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      page: 1,
      Pagelength: 1,

      addcat: false,
      PaymentList: [],
      paymentDialog: false,
      monthNames: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      paymentTypeList: ["Cash", "QR Code", "Swiping", "Bank Transfer", "Cheque"],
      paytype: null,
      paymentDetails1: {},
      schemeDetails: {},
      dateMenu: false,
      customPaidDate: new Date().toISOString().substr(0, 10),
      todayDate: new Date().toISOString().substr(0, 10),
      reverseDialog: false,
      selectedReverseId: null,
      payAllMode: false,
    };
  },
  mounted() {
    this.schemePayment();
  },
  computed: {
    monthlyDepositAmount() {
      if (this.schemeDetails && this.schemeDetails.goldSchemeId && this.schemeDetails.goldSchemeId.minmonthlydeposit) {
        return this.schemeDetails.goldSchemeId.minmonthlydeposit;
      }
      if (this.PaymentList && this.PaymentList.length > 0 && this.PaymentList[0].monthlyFixedAmount) {
        return this.PaymentList[0].monthlyFixedAmount;
      }
      return 0;
    },
    totalInstallmentsCount() {
      return this.PaymentList ? this.PaymentList.length : 0;
    },
    totalPaidCount() {
      if (!this.PaymentList) return 0;
      return this.PaymentList.filter((item) => item.isPaid === true).length;
    },
    totalPaidAmountSum() {
      if (!this.PaymentList) return 0;
      return this.PaymentList.reduce((acc, item) => {
        return acc + (item.isPaid ? Number(item.amountPaid || item.monthlyFixedAmount || 0) : 0);
      }, 0);
    },
    totalAccruedInterest() {
      if (!this.PaymentList) return 0;
      return this.PaymentList.reduce((acc, item) => {
        return acc + (item.isPaid && item.monthlyInterestAmount ? Number(item.monthlyInterestAmount) : 0);
      }, 0);
    },
    pendingInstallmentsCount() {
      if (!this.PaymentList) return 0;
      return this.PaymentList.filter((item) => !item.isPaid).length;
    },
    totalPendingAmount() {
      if (!this.PaymentList) return 0;
      return this.PaymentList.reduce((acc, item) => {
        return acc + (!item.isPaid ? Number(item.monthlyFixedAmount || 0) : 0);
      }, 0);
    },
  },
  watch: {
    page() {
      this.schemePayment();
    },
  },
  methods: {
    getCustomerInitials(name) {
      if (!name) return "CU";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    },
    formatCurrency(val) {
      if (val == null || isNaN(val)) return "0";
      return Number(val).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      });
    },
    formatDecimals(val) {
      if (val == null || isNaN(val)) return "0.00";
      return Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(item) {
      if (!item) return "-";
      const dt = new Date(item);
      if (isNaN(dt.getTime())) return "-";
      const day = dt.getDate();
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = monthNames[dt.getMonth()];
      const year = dt.getFullYear();
      return `${day < 10 ? "0" + day : day} ${month} ${year}`;
    },
    isPreviousMonth(paidMonth, paidYear) {
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();

      const paidMonthIndex = this.monthNames.indexOf(paidMonth);
      const parsedYear = parseInt(paidYear, 10);

      if (isNaN(parsedYear)) return true;

      return (
        parsedYear < currentYear ||
        (parsedYear === currentYear && paidMonthIndex <= currentMonth)
      );
    },
    schemePayment() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/goldscheme/customer/paymentgetlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          page: this.page,
          count: 20,
          goldschemecustomerId: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.PaymentList = response.data.data || [];
            this.schemeDetails = response.data.schemeDetails || {};
            this.Pagelength = Math.ceil((response.data.count || 20) / 20);
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg || "Failed to load scheme payments";
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
          this.ServerError = true;
        });
    },
    openPayAll() {
      this.payAllMode = true;
      this.paymentDetails1 = {};
      this.paytype = "Cash";
      this.customPaidDate = new Date().toISOString().substr(0, 10);
      this.paymentDialog = true;
    },
    makePayment2(value) {
      this.payAllMode = false;
      this.paymentDetails1 = value;
      this.paytype = "Cash";
      this.customPaidDate = new Date().toISOString().substr(0, 10);
      this.paymentDialog = true;
    },
    valid() {
      if (!this.paytype) {
        this.msg = "Please choose a payment method";
        this.showsnackbar = true;
        return;
      }
      this.MakePayment1();
    },
    resetForm() {
      if (this.$refs.addcat) {
        this.$refs.addcat.reset();
      }
      this.paytype = null;
    },
    MakePayment1() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: this.payAllMode ? "/goldscheme/payment/payall" : "/goldscheme/payment",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: this.payAllMode
          ? {
              goldSchemecustomerId: this.$route.query.id,
              paymentType: this.paytype,
              goldRate: localStorage.getItem("goldRate"),
              paidDate: this.customPaidDate,
            }
          : {
              amountPaid: this.paymentDetails1.monthlyFixedAmount,
              paymentType: this.paytype,
              payId: this.paymentDetails1._id,
              goldRate: localStorage.getItem("goldRate"),
              paidDate: this.customPaidDate,
            },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.paymentDialog = false;
            this.payAllMode = false;
            this.paytype = "";
            this.showsnackbar = true;
            this.msg = response.data.msg || "Payment recorded successfully";
            this.schemePayment();
          } else if (response.data.status === false && response.data.isOverDue === true) {
            this.schemePayment();
            this.showsnackbar = true;
            this.msg = response.data.msg || "This scheme installment is overdue";
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg || "Payment failed";
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
          this.showsnackbar = true;
          this.msg = "Server error while recording payment";
        });
    },
    downloadReceipt(payId) {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/download/goldschemereceipt",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          payId: payId,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `GoldSchemeReceipt_${payId}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
          this.showsnackbar = true;
          this.msg = "Failed to download receipt";
        });
    },
    reversePayment() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/goldscheme/payment/reverse",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          payId: this.selectedReverseId,
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.reverseDialog = false;
          this.showsnackbar = true;
          this.msg = response.data.msg || "Payment reversed successfully";
          if (response.data.status) {
            this.schemePayment();
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
          this.reverseDialog = false;
          this.showsnackbar = true;
          this.msg = "Server error while reversing payment";
        });
    },
  },
};
</script>

<style scoped>
.scheme-payment-wrapper {
  color: #1e293b;
}

/* Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.back-btn {
  background: #f1f5f9 !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #e2e8f0 !important;
}

.header-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #fde047;
}

.page-title {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.refresh-icon-btn {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
}

.refresh-icon-btn:hover {
  background: #f1f5f9 !important;
}

.erp-btn-secondary {
  border-color: #cbd5e1 !important;
  color: #475569 !important;
  font-weight: 600 !important;
  font-size: 12.5px !important;
}

/* Status Badges */
.status-pill {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 3px 10px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
}

.status-active {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.status-closed {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.overdue-warning-pill {
  background: #fff1f2;
  color: #e11d48;
  border: 1px solid #fecdd3;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
}

/* Customer Profile Card */
.customer-profile-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.customer-avatar-monogram {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%);
  color: #ffffff;
  font-weight: 800;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(154, 106, 44, 0.25);
  flex-shrink: 0;
}

.customer-name-heading {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.customer-phone-sub {
  font-size: 12.5px;
  color: #64748b;
  display: flex;
  align-items: center;
  margin-top: 2px;
}

.customer-info-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12.5px;
}

.info-label {
  color: #64748b;
  font-weight: 500;
}

.info-value {
  color: #0f172a;
  font-weight: 600;
  text-align: right;
}

.scheme-tag {
  background: #fefce8;
  color: #9a6b27;
  border: 1px solid #fef08a;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11.5px;
  display: inline-flex;
  align-items: center;
}

/* KPI Cards */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  height: 100%;
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  min-width: 0;
}

.kpi-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin-bottom: 2px;
}

.kpi-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 19px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.kpi-unit {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.highlight-gold {
  color: #b45309;
}

.kpi-hint {
  font-size: 11.5px;
  color: #94a3b8;
  margin-top: 2px;
}

.kpi-icon-pill {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Table Card */
.table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.card-top-bar {
  background: #fafafa;
}

.card-heading {
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #0f172a;
  text-transform: uppercase;
}

.items-count-badge {
  background: #f1f5f9;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 9999px;
  border: 1px solid #e2e8f0;
}

.table-responsive {
  overflow-x: auto;
}

.scheme-ledger-table thead {
  background-color: #f8fafc;
}

.scheme-ledger-table thead th {
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569 !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 12px 14px !important;
  white-space: nowrap;
}

.scheme-ledger-table tbody td {
  font-size: 12.5px !important;
  color: #1e293b;
  border-bottom: 1px solid #f1f5f9 !important;
  padding: 10px 14px !important;
  white-space: nowrap;
}

.table-row-hover:hover {
  background-color: #f8fafc;
}

.font-weight-semibold {
  font-weight: 600;
}

.text-muted {
  color: #94a3b8;
}

.text-emerald {
  color: #16a34a;
}

.date-chip {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 11.5px;
  color: #475569;
  font-weight: 500;
}

.rate-badge {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #dbeafe;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

/* Status Chips in Table */
.badge-status-paid {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
  padding: 3px 9px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
}

.badge-status-due {
  background: #fefce8;
  color: #b45309;
  border: 1px solid #fde68a;
  padding: 3px 9px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
}

.badge-status-overdue {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fca5a5;
  padding: 3px 9px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
}

.badge-status-upcoming {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
  padding: 3px 9px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
}

/* Action Buttons */
.btn-pay-now {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  height: 30px !important;
  border-radius: 6px !important;
  padding: 0 12px !important;
  box-shadow: 0 2px 6px rgba(154, 106, 44, 0.25) !important;
}

.btn-receipt-action {
  border-radius: 6px !important;
  font-size: 11.5px !important;
  font-weight: 600 !important;
  height: 28px !important;
  padding: 0 8px !important;
  border-color: #86efac !important;
}

.btn-receipt-action:hover {
  background: #f0fdf4 !important;
}

.btn-reverse-action {
  background: #fef2f2 !important;
  border: 1px solid #fecaca !important;
  border-radius: 6px !important;
  width: 28px !important;
  height: 28px !important;
}

.btn-reverse-action:hover {
  background: #fee2e2 !important;
}

.border-t {
  border-top: 1px solid #f1f5f9;
}

/* Modals */
.modal-card {
  background: #ffffff !important;
}

.modal-header {
  background: #f8fafc;
}

.modal-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.modal-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.modal-icon-pill {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 5px;
}

.required-star {
  color: #dc2626;
}

.pro-input >>> .v-input__slot {
  min-height: 40px !important;
  border-radius: 8px !important;
  font-size: 13.5px !important;
}

.erp-readonly >>> .v-input__slot {
  background-color: #f8fafc !important;
}

.bg-modal-footer {
  background-color: #f8fafc;
}

.btn-cancel {
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 12.5px !important;
}

.btn-primary {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  box-shadow: 0 3px 10px rgba(154, 106, 44, 0.28) !important;
}

.delete-icon-box {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #fef2f2;
  border: 1px solid #fecaca;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.delete-subtitle {
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.5;
  max-width: 320px;
  margin: 0 auto;
}

@media (max-width: 960px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}
</style>